import type {
   ColumnMap,
   CustomField,
   SortModel,
   StreamResponseConversion,
} from "@/react/prop-types";
import { FilterFieldType } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";
import type { CellValueChangeParams, DataTableConfig } from "@procore/data-table";
import {
   convertDataTableFilters,
   createDefaultColumnState,
   type ListServerFilter,
   sortOrderMap,
} from "../data-table/data-table-utils";
import { FieldMapping, FindPeopleSortBy } from "./people-list-enums";
import type { CoreApiSort } from "./people-list-prop-types";
import { CustomFieldType } from "@laborchart-modules/common";
import { PersonStore } from "@/stores/person-store.core";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";

import { DateUtils } from "@/lib/utils/date";
import { getTagExpirationState } from "@/react/shared/helper";

import type { SerializedPeopleListPerson } from "@laborchart-modules/lc-core-api/dist/api/people/find-people";
import { PersonStatus } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { useI18nContext } from "@procore/core-react";
import type { CustomFieldProjectType } from "@/react/prop-types";
import { Format } from "@/lib/utils/format";
import { convertUtcDateValueToDateWithoutTimezone } from "@/lib/utils/date-2";

export function convertDataTableSort(
   sort: SortModel[],
   isLastNameFirst: boolean = false,
): CoreApiSort {
   sortFieldMaps.person = isLastNameFirst
      ? FindPeopleSortBy.PERSON_NAME_LAST
      : FindPeopleSortBy.PERSON_NAME_FIRST;
   if (sort.length == 0) return {};
   const sortObj: CoreApiSort = {
      sort_by: sortFieldMaps[sort[0].field],
      direction: sortOrderMap[sort[0].sort],
   };
   // If the sort field is custom_field, then add the custom_field_id
   // fieldId will only be there in case of custom_field, it has been added explicitly to identify the custom_field
   if (sort[0].fieldId) {
      sortObj["sort_by"] = sortFieldMaps["custom_field"];
      sortObj["custom_field_id"] = sort[0].fieldId;
   }
   return sortObj;
}

export const defaultPeopleListTableConfig: DataTableConfig = {
   columnState: [
      {
         ...createDefaultColumnState(),
         field: "person",
         sort: "asc",
         sortIndex: 0,
         width: 250,
      },
      {
         ...createDefaultColumnState(),
         field: "employee_number",
         width: 240,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "permission",
         width: 230,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "type",
         width: 230,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "job_title",
         width: 250,
      },
      {
         ...createDefaultColumnState(),
         field: "classification",
         hidden: true,
         width: 250,
      },
      {
         ...createDefaultColumnState(),
         field: "current_assignments_project_name",
      },
      {
         ...createDefaultColumnState(),
         field: "current_assignments_job_number",
      },
      {
         ...createDefaultColumnState(),
         field: "current_assignments_start_date",
      },
      {
         ...createDefaultColumnState(),
         field: "current_assignments_end_date",
      },
      {
         ...createDefaultColumnState(),
         field: "next_assignment_project_name",
      },
      {
         ...createDefaultColumnState(),
         field: "next_assignment_job_number",
      },
      {
         ...createDefaultColumnState(),
         field: "next_assignment_start_date",
      },
      {
         ...createDefaultColumnState(),
         field: "next_assignment_end_date",
      },
      {
         ...createDefaultColumnState(),
         field: "assignments",
         width: 250,
      },
      {
         ...createDefaultColumnState(),
         field: "availability_until",
         width: 250,
      },
      {
         ...createDefaultColumnState(),
         field: "address_1",
         width: 368,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "address_2",
         width: 368,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "city_town",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "state_province",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "zipcode",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "country",
         width: 200,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "tag_instances",
         width: 140,
      },
      {
         ...createDefaultColumnState(),
         field: "status",
         width: 140,
      },
      {
         ...createDefaultColumnState(),
         field: "email",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "hourly_wage",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "notification_profile",
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "phone",
         width: 200,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "gender",
         width: 200,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "language",
         width: 230,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "hired_date",
         width: 200,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "dob",
         width: 200,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "can_receive_email",
         width: 100,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "can_receive_mobile",
         width: 100,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "can_receive_sms",
         width: 100,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "groups",
         width: 140,
         hidden: true,
      },
      {
         ...createDefaultColumnState(),
         field: "linked_to_procore",
         width: 50,
         hidden: true,
      },
   ],
   columnGroupState: [
      {
         groupId: "current_assignment",
         open: false,
      },
      {
         groupId: "next_assignment",
         open: false,
      },
   ],
   filters: {},
   rowHeight: 49,
   serverFilters: [],
};

export function filterGroupDetails({
   groupOptions,
   personGroupList,
   isAdmin,
}: {
   groupOptions: StreamResponseConversion | null;
   personGroupList: string[];
   isAdmin: boolean;
}) {
   if (isAdmin) {
      return groupOptions;
   }
   // Filter the groupOptions based on the personGroupList
   const filtered = groupOptions?.filter((groupOption) => personGroupList.includes(groupOption.id));
   return filtered;
}

const DATE_FIELDS: any[] = ["hired_date"];
const NUMERIC_FIELDS: any[] = ["hourly_wage"];
const NESTED_FIELDS_TYPE: any[] = [FilterFieldType.BOOL, FilterFieldType.MULTI_SELECT];
const NUMERIC_FIELDS_TYPE: any[] = [FilterFieldType.NUMBER, FilterFieldType.CURRENCY];

export const EMAIL_VALIDATION_ERROR = "Email is already in use.";
export const EMPLOYEE_NUMBER_VALIDATION_ERROR = "Employee number is already in use.";

export const convertPeopleDataTableFilters = (filters: ListServerFilter[]) =>
   convertDataTableFilters(
      filters,
      {
         nameMapper: filterNameMaps,
         typeMapper: filterTypeMaps,
         propertyMapper: filterPropertyMaps,
      },
      {
         numeric: NUMERIC_FIELDS,
         nestedTypes: NESTED_FIELDS_TYPE,
         numericTypes: NUMERIC_FIELDS_TYPE,
         date: DATE_FIELDS,
      },
   );

export const filterNameMaps: { [key: string]: string } = {
   hired_date: "People Hired Date",
   status: "People Status",
   tag_instances: "People Tags",
   job_title: "People Job Titles",
   gender: "People Gender",
   type: "People Type",
   hourly_wage: "People Hourly Wage",
   permission: "People Permission",
};

export const filterFieldMap: ColumnMap = {
   tag_instances: "tag_instances",
   status: "status",
   position_id: "job_title",
   is_male: "gender",
   person_type: "type",
   hourly_wage: "hourly_wage",
   permission_level_id: "permission",
};

export const columnHeadersMap: ColumnMap = {
   name: "person",
   permission: "permission",
   employee_number: "employee_number",
   position: "job_title",
   classification_id: "classification",
   address_1: "address_1",
   address_2: "address_2",
   city_town: "city_town",
   state_province: "state_province",
   zip_code: "zipcode",
   zipcode: "zipcode",
   type: "project_type",
   status: "status",
   customer_name: "customer_name",
   created_by: "created_by",
   created_at: "created_at",
   country: "country",
   start_date: "start_date",
   est_end_date: "est_end_date",
   daily_start_time: "daily_start_time",
   daily_end_time: "daily_end_time",
   dob: "dob",
   timezone: "timezone",
   bid_rate: "bid_rate",
   percent_complete: "percent_complete",
   wage_overrides: "wage_overrides",
   groups: "groups",
   tag_instances: "tag_instances",
   procore_id: "procore_id",
   next_assignment: "next_assignment",
   current_assignments: "current_assignments",
   assignments: "assignments",
   availability_until: "availability_until",
   email: "email",
   hourly_wage: "hourly_wage",
   notification_profile_id: "notification_profile",
   phone: "phone",
   is_male: "gender",
   language: "language",
   hired_date: "hired_date",
   can_receive_email: "can_receive_email",
   can_recieve_email: "can_receive_email",
   can_receive_mobile: "can_receive_mobile",
   can_recieve_mobile: "can_receive_mobile",
   can_recieve_sms: "can_receive_sms",
   can_receive_sms: "can_receive_sms",
   has_procore_mapping: "linked_to_procore",
   emergency_contact_email: "emergency_contact_email",
   emergency_contact_name: "emergency_contact_name",
   current_assignments_project_name: "current_assignments_project_name",
   next_assignment_project_name: "next_assignment_project_name",
};

export const subPropertiesColumnNameMap: ColumnMap = {
   assignment_job_number: "job_number",
   assignment_start_date: "start_date",
   assignment_end_date: "end_date",
};
export const subPropertiesColumnHeaderMap: ColumnMap = {
   current_assignments_job_number: "current_assignments_job_number",
   current_assignments_start_date: "current_assignments_start_date",
   current_assignments_end_date: "current_assignments_end_date",
   next_assignment_job_number: "next_assignment_job_number",
   next_assignment_start_date: "next_assignment_start_date",
   next_assignment_end_date: "next_assignment_end_date",
};

// Define the sub-property configurations for current and next assignments
export const subPropertiesConfig = {
   current_assignment: [
      {
         field: subPropertiesColumnHeaderMap.current_assignments_job_number,
         name: "Project Number",
         key: subPropertiesColumnNameMap.assignment_job_number,
      },
      {
         field: subPropertiesColumnHeaderMap.current_assignments_start_date,
         name: "Start Date",
         key: subPropertiesColumnNameMap.assignment_start_date,
      },
      {
         field: subPropertiesColumnHeaderMap.current_assignments_end_date,
         name: "End Date",
         key: subPropertiesColumnNameMap.assignment_end_date,
      },
   ],
   next_assignment: [
      {
         field: subPropertiesColumnHeaderMap.next_assignment_job_number,
         name: "Project Number",
         key: subPropertiesColumnNameMap.assignment_job_number,
      },
      {
         field: subPropertiesColumnHeaderMap.next_assignment_start_date,
         name: "Start Date",
         key: subPropertiesColumnNameMap.assignment_start_date,
      },
      {
         field: subPropertiesColumnHeaderMap.next_assignment_end_date,
         name: "End Date",
         key: subPropertiesColumnNameMap.assignment_end_date,
      },
   ],
};

const filterTypeMaps: { [key: string]: FilterFieldType } = {
   hired_date: FilterFieldType.DATE,
   job_title: FilterFieldType.SELECT,
   status: FilterFieldType.SELECT,
   gender: FilterFieldType.BOOL,
   type: FilterFieldType.SELECT,
   hourly_wage: FilterFieldType.NUMBER,
   tag_instances: FilterFieldType.MULTI_SELECT,
   permission: FilterFieldType.SELECT,

   date: FilterFieldType.DATE,
   bool: FilterFieldType.BOOL,
   currency: FilterFieldType.CURRENCY,
   "multi-select": FilterFieldType.MULTI_SELECT,
   number: FilterFieldType.NUMBER,
   select: FilterFieldType.SELECT,
   text: FilterFieldType.TEXT,
};

const filterPropertyMaps: { [key: string]: string } = {
   job_title: "position_id",
   status: "status",
   gender: "is_male",
   type: "person_type",
   hourly_wage: "hourly_wage",
   tag_instances: "tag_instances",
   permission: "permission_level_id",
};

export const sortFieldMaps: { [key: string]: FindPeopleSortBy } = {
   person: FindPeopleSortBy.PERSON_NAME_FIRST,
   permission: FindPeopleSortBy.PERMISSION,
   job_title: FindPeopleSortBy.JOB_TITLE,
   status: FindPeopleSortBy.PERSON_STATUS,
   email: FindPeopleSortBy.EMAIL,
   hourly_wage: FindPeopleSortBy.HOURLY_WAGE,
   address_1: FindPeopleSortBy.ADDRESS_1,
   address_2: FindPeopleSortBy.ADDRESS_2,
   city_town: FindPeopleSortBy.CITY_TOWN,
   state_province: FindPeopleSortBy.STATE_PROVINCE,
   zipcode: FindPeopleSortBy.ZIPCODE,
   hired_date: FindPeopleSortBy.HIRED_DATE,
   language: FindPeopleSortBy.LANGUAGE,
   employee_number: FindPeopleSortBy.EMPLOYEE_NUMBER,
   phone: FindPeopleSortBy.PHONE,
   dob: FindPeopleSortBy.DOB,
   can_receive_email: FindPeopleSortBy.CAN_RECIEVE_EMAIL,
   can_receive_mobile: FindPeopleSortBy.CAN_RECIEVE_MOBILE,
   can_receive_sms: FindPeopleSortBy.CAN_RECIEVE_SMS,
   custom_field: FindPeopleSortBy.CUSTOM_FIELD,
   country: FindPeopleSortBy.COUNTRY,
   notification_profile: FindPeopleSortBy.NOTIFICATION_PROFILE,
   availability_until: FindPeopleSortBy.CURRENT_ASSIGNMENTS,
   current_assignments_project_name: FindPeopleSortBy.CURRENT_ASSIGNMENTS,
   current_assignments_job_number: FindPeopleSortBy.CURRENT_ASSIGNMENTS_JOB_NUMBER,
   current_assignments_start_date: FindPeopleSortBy.CURRENT_ASSIGNMENTS_START_DATE,
   current_assignments_end_date: FindPeopleSortBy.CURRENT_ASSIGNMENTS_END_DATE,
   next_assignment_project_name: FindPeopleSortBy.NEXT_ASSIGNMENT,
   next_assignment_job_number: FindPeopleSortBy.NEXT_ASSIGNMENT_JOB_NUMBER,
   next_assignment_start_date: FindPeopleSortBy.NEXT_ASSIGNMENT_START_DATE,
   next_assignment_end_date: FindPeopleSortBy.NEXT_ASSIGNMENT_END_DATE,
};

export const fieldTranslationKeyMap: ColumnMap = {
   person: "views.company.workforce_planning.people.person_name",
   employee_number: "views.company.workforce_planning.people.employee_id",
   permission: "views.company.workforce_planning.people.permission",
   job_title: "views.company.workforce_planning.people.job_title",
   classification: "views.company.workforce_planning.people.classification",
   address_1: "views.company.workforce_planning.people.address",
   address_2: "views.company.workforce_planning.people.address_2",
   city_town: "views.company.workforce_planning.people.city",
   state_province: "views.company.workforce_planning.people.state",
   zipcode: "views.company.workforce_planning.people.postal",
   country: "views.company.workforce_planning.people.country",
   tag_instances: "views.company.workforce_planning.people.tags",
   status: "views.company.workforce_planning.people.status",
   email: "views.company.workforce_planning.people.email",
   hourly_wage: "views.company.workforce_planning.people.hourly_wage",
   notification_profile: "views.company.workforce_planning.people.notification_profile",
   phone: "views.company.workforce_planning.people.phone_number",
   gender: "views.company.workforce_planning.people.gender",
   language: "views.company.workforce_planning.people.language",
   hired_date: "views.company.workforce_planning.people.hired_date",
   dob: "views.company.workforce_planning.people.birth_date",
   can_receive_email: "views.company.workforce_planning.people.receive_email",
   can_receive_mobile: "views.company.workforce_planning.people.receive_mobile",
   can_receive_sms: "views.company.workforce_planning.people.receive_sms",
   groups: "views.company.workforce_planning.people.groups",
   linked_to_procore: "views.company.workforce_planning.people.linked_to_procore",
   assignments: "views.company.workforce_planning.people.assignments",
   availability_until: "views.company.workforce_planning.people.availability_until",
   current_assignments: "views.company.workforce_planning.people.current_assignment",
   next_assignment: "views.company.workforce_planning.people.next_assignment",
   project_name: "views.company.workforce_planning.people.project_name",
   job_number: "views.company.workforce_planning.people.project_number",
   start_date: "views.company.workforce_planning.people.start_date",
   end_date: "views.company.workforce_planning.people.end_date",
};

export const legacyFieldNamesMap: ColumnMap = {
   person: "name",
   employee_number: "employee_number",
   permission: "permission",
   job_title: "position",
   classification: "classification_id",
   address_1: "address_1",
   address_2: "address_2",
   city_town: "city_town",
   state_province: "state_province",
   zipcode: "zipcode",
   country: "country",
   tag_instances: "tag_instances",
   status: "status",
   email: "email",
   hourly_wage: "hourly_wage",
   notification_profile: "notification_profile_id",
   phone: "phone",
   gender: "is_male",
   language: "language",
   hired_date: "hired_date",
   dob: "dob",
   can_receive_email: "can_recieve_email",
   can_receive_mobile: "can_recieve_mobile",
   can_receive_sms: "can_recieve_sms",
   groups: "group_ids",
   linked_to_procore: "has_procore_mapping",
   first_name: "first_name",
   last_name: "last_name",
   type: "person_type",
   is_user: "person_type",
   is_assignable: "person_type",
   classification_id: "classification_id",
   emergency_contact_name: "emergency_contact_name",
   emergency_contact_number: "emergency_contact_number",
   emergency_contact_relation: "emergency_contact_relation",
   emergency_contact_email: "emergency_contact_email",
   permission_level_id: "permission_level_id",
   position_id: "position_id",
   job_title_id: "position_id",
   // This naming should be fleshed out once the legacy people list is gone.
   current_assignments: "current_assignments",
   availability_until: "availability_until",
   assignments: "assignments_count",
   next_assignment: "next_assignment",
};

// added this function over here to improve testability
export const updatePeople = async (
   params: CellValueChangeParams<any>,
   customFields: CustomField[] | null,
) => {
   const { id } = params.rowData;
   const { field: fieldName, newValue } = params;

   // Build payload based on field type and custom field properties
   const payload = { id, ...buildFieldPayload(fieldName, newValue, customFields) };

   try {
      // Send the payload to PersonStore and wait for the response
      await PersonStore.updatePeopleStream([payload]).stream;
      return true;
   } catch (e: any) {
      return e.validation?.[0].message;
   }
};

// Helper to build field-specific parts of the payload
export const buildFieldPayload = (
   fieldName: string,
   newValue: any,
   customFields: CustomField[] | null,
) => {
   switch (fieldName) {
      case FieldMapping.DOB:
      case FieldMapping.HIRED_DATE:
         return { [fieldName]: newValue ? newValue.getTime() : null };

      case FieldMapping.GENDER:
      case FieldMapping.STATUS:
         return { [fieldName]: newValue ? newValue.id.toLowerCase() : null };

      case FieldMapping.HOURLY_WAGE:
         return { [fieldName]: newValue ? parseFloat(newValue) : null };

      case FieldMapping.GROUPS:
         return {
            [legacyFieldNamesMap["groups"]]: newValue
               ? newValue.map(({ id }: { id: string }) => id)
               : null,
         };

      case FieldMapping.JOB_TITLE:
         return { [fieldName]: newValue.id !== "" ? newValue.id : null };

      case FieldMapping.CLASSIFICATION:
         return { classification_id: newValue?.id ?? null };

      default: {
         const customFieldPayload = handleCustomField(fieldName, newValue, customFields);
         return customFieldPayload
            ? { custom_fields: customFieldPayload }
            : { [fieldName]: newValue };
      }
   }
};

// Process custom fields and return the custom field payload
export const handleCustomField = (
   fieldName: string,
   value: any,
   customFields: CustomField[] | null,
) => {
   const customField = customFields?.find((x) => x.integration_name === fieldName);
   if (!customField) return null;

   let customFieldValue = value;
   if (customField.type === CustomFieldType.HEX_COLOR) {
      customFieldValue = value.color;
   } else if (customField.type === CustomFieldType.DATE) {
      customFieldValue = value ? getDetachedDay(value) : null;
   } else if (
      customField.type === CustomFieldType.NUMBER ||
      customField.type === CustomFieldType.CURRENCY
   ) {
      customFieldValue = Number(value);
   } else if (customField.type === CustomFieldType.BOOL) {
      customFieldValue = value;
      //if value set by valueSetter
      if (value == "on") customFieldValue = true;
      else if (value == "off") customFieldValue = false;
   }

   return { [customField.id]: customFieldValue };
};

export const setPersonStatus = (status: PersonStatus) => {
   const statusMap: Record<PersonStatus, { label: string; color: string }> = {
      [PersonStatus.ACTIVE]: {
         label: "views.company.workforce_planning.active",
         color: "green",
      },
      [PersonStatus.INACTIVE]: {
         label: "views.company.workforce_planning.inactive",
         color: "gray",
      },
   };

   return {
      id: status,
      ...statusMap[status],
   };
};

export const setDateValue = (value?: number | null) =>
   value ? convertUtcDateValueToDateWithoutTimezone(value) : null;

export const setPersonType = (isUser: boolean, isAssignable: boolean) => {
   if (isUser && isAssignable) {
      return "assignable-user";
   } else if (isAssignable) {
      return "assignable";
   } else if (isUser) {
      return "user";
   }
};

export const setTagsValue = (tags: SerializedPeopleListPerson["tag_instances"]) => {
   return tags?.map((tag) => ({
      label: tag.tag.abbreviation,
      color: tag.tag.color,
      id: tag.tag.id,
      name: tag.tag.name,
      expirationState: getTagExpirationState({
         require_expr_date: tag.tag.require_expr_date,
         expr_days_warning: tag.tag.expr_days_warning,
         expr_date: tag.expr_date,
      }),
      shape: "pill",
      componentName: "multiselect",
   }));
};

export const setJobValue = (position: SerializedPeopleListPerson["position"]) => {
   return {
      label: position?.name ?? "",
      color: position?.color ?? "",
      shape: "circle",
      id: position?.id ?? "",
   };
};

export const setPersonValue = (person: SerializedPeopleListPerson) => {
   return {
      name: {
         first: person.name.first ?? " ",
         last: person.name.last ?? " ",
      },
      profile_pic_url: person.profile_pic_url,
   };
};
export const setPersonGender = (
   gender: SerializedPeopleListPerson["gender"],
   I18n: ReturnType<typeof useI18nContext>,
) => ({
   id: gender,
   label:
      gender == "male"
         ? I18n.t("views.company.workforce_planning.male")
         : I18n.t("views.company.workforce_planning.female"),
});

export function mapApiPeopleToTablePeople(
   peopleData: SerializedPeopleListPerson[],
   groupOptions: StreamResponseConversion,
   I18n: ReturnType<typeof useI18nContext>,
) {
   const peopleListDetails: Array<Record<string, any>> = [];
   for (const personData of peopleData) {
      const personListOptions = filterGroupDetails({
         groupOptions,
         personGroupList: personData.group_ids ?? [],
         isAdmin: personData.permission_level?.is_admin ?? false,
      } as any);

      const currentAssignments = personData.current_assignments
         ? personData.current_assignments
         : [];

      const nextAssignments = Array.isArray(personData.next_assignment)
         ? personData.next_assignment
         : personData.next_assignment
         ? [personData.next_assignment]
         : [];

      const assignments = [...currentAssignments, ...nextAssignments];

      // Initialize the base people details
      const personDetails: Record<string, any> = {
         ...personData,
         person: setPersonValue(personData),
         permission: personData.permission_level?.name,
         job_title: setJobValue(personData.position),
         tag_instances: setTagsValue(personData.tag_instances ?? []),
         type: setPersonType(personData.is_user, personData.is_assignable),
         status: setPersonStatus(personData.status),
         email: personData.email,
         hourly_wage: personData.hourly_wage,
         address_1: personData.address_1,
         address_2: personData.address_2,
         city: personData.city_town,
         state: personData.state_province,
         country: personData.country,
         postal: personData.zipcode,
         hired_date: setDateValue(personData.hired_date),
         linked_to_procore: {
            id: personData.procore_id,
            entityType: personData.procore_target_type,
            searchTerm: personData.name.last,
         },
         language: Format.capitalize(personData.language),
         notification_profile: personData.notification_profile?.name,
         gender: personData.gender ? setPersonGender(personData.gender, I18n) : null,
         dob: setDateValue(personData.dob),
         groups: personListOptions,
         assignments,
         current_assignments: currentAssignments,
         next_assignments: nextAssignments,
      };

      // Adding custom fields as individual attributes
      if (personData.custom_fields) {
         personData.custom_fields.forEach((customField: CustomFieldProjectType) => {
            const { integration_name, value, type } = customField;
            if (type === CustomFieldType.DATE && value) {
               personDetails[integration_name] = DateUtils.getAttachedDate(value);
            } else if (type === CustomFieldType.HEX_COLOR && value) {
               personDetails[integration_name] = {
                  label: "",
                  color: value,
                  shape: "square",
               };
            } else {
               personDetails[integration_name] = value;
            }
         });
      }
      peopleListDetails.push(personDetails);
   }

   return peopleListDetails;
}
